/** @format */

import {handleChange, InputWithErrorHandler, SelectWithErrorHandler} from 'frontier-form';
import {useVerifyPaymentProfile} from './use_verify_payment_profile';
import {CheckboxWithErrorHandler} from '../../../components/form';
import {useSavePaymentProfile} from './use_save_payment_profile';
import {Drawer, DrawerHeader} from '@sttark/design-system';
import {IfElse, ErrorDisplay} from 'frontier-utilities';
import React, {useState, useEffect} from 'react';
import creditCardType from 'credit-card-type';
import update from 'immutability-helper';

// styles
import './add_profile_forms.scss';

export default function (props) {
    const {onSuccessfulSave, onErrorSaving} = props;

    const [nonceData, setNonceData] = useState(null);
    const [isOpen, setIsOpen] = useState(false);
    const [formData, setFormData] = useState({});
    const [error, setError] = useState('');

    // verifies that the payment method is valid
    const {
        isLoading: isVerifyingPaymentMethod,
        handleVerifyPaymentProfile,
        error: verifyPaymentError,
        data: verifyPaymentProfileData
    } = useVerifyPaymentProfile({
        cardData: {
            cardNumber: String(formData.account_number).replaceAll(/\D/gm, ''),
            month: String(formData.expires_month),
            cardCode: String(formData.card_code),
            year: String(formData.expires_year)
        }
    });

    // saves the payment method record to the database
    const {
        loading: isSavingPaymentProfile,
        handleSavePaymentProfile,
        error: savePaymentError,
        data: savePaymentData
    } = useSavePaymentProfile({
        ...formData,
        account_number: formData.account_number?.trim()?.slice(-4) // send only the last 4 digits
    });

    const handleClose = () => {
        setNonceData(null);
        setIsOpen(false);
        setFormData({});
        setError('');
    };

    const handleChange = updateTarget => {
        setFormData(update(formData, updateTarget));
    };

    // clone the child and add the onClick event to open the drawer
    const enhancedTriggerChild = React.isValidElement(props.children)
        ? React.cloneElement(props.children, {
              onClick: () => setIsOpen(true)
          })
        : null;

    // let the parent component know that the payment method was saved
    useEffect(() => {
        if (savePaymentData) {
            onSuccessfulSave(savePaymentData);
            handleClose();
        }
    }, [savePaymentData]);

    useEffect(() => {
        if (savePaymentError) {
            if (onErrorSaving) onErrorSaving(savePaymentError);
            setError(savePaymentError);
        } else if (verifyPaymentError) {
            setError(verifyPaymentError);
        }
    }, [savePaymentError, verifyPaymentError]);

    useEffect(() => {
        if (verifyPaymentProfileData) {
            setNonceData(verifyPaymentProfileData);
        }
    }, [verifyPaymentProfileData]);
    // update the form data with the nonce data
    useEffect(() => {
        if (nonceData) {
            setFormData({
                ...formData,
                card_type: creditCardType(formData.account_number)?.[0]?.niceType,
                expires_month: String(formData.expires_month),
                expires_year: String(formData.expires_year),
                opaqueData: nonceData.opaqueData
            });
            setError('');
        }
    }, [nonceData]);

    return (
        <>
            <Drawer isOpen={isOpen} onClose={handleClose} className="add-credit-profile-form-03mc">
                <DrawerHeader>
                    <div className="add-credit-profile-form-03mc__header d-flex align-items-center justify-content-between">
                        <h3 className="m-0 p-0">Add a New Payment Card</h3>
                        <div className="d-flex align-items-center justify-content-end gap-4">
                            <button onClick={handleClose} className="btn btn--secondary">
                                <b>Cancel</b>
                            </button>
                            <IfElse condition={isSavingPaymentProfile}>
                                <button className="btn btn--primary" disabled="disabled">
                                    <b>Saving...</b>
                                </button>
                                <button
                                    onClick={handleSavePaymentProfile}
                                    disabled={nonceData === null}
                                    className="btn btn--primary">
                                    <b>Add Card</b>
                                </button>
                            </IfElse>
                        </div>
                    </div>
                </DrawerHeader>
                <Form onChange={handleChange} formData={formData} error={error} />
                <div className="add-credit-profile-form-03mc__alert">
                    <IfElse condition={!!nonceData}>
                        <aside className="alert alert-success" role="alert">
                            Payment method verified. You can save now.
                        </aside>
                        <IfElse condition={!isVerifyingPaymentMethod}>
                            <button
                                onClick={handleVerifyPaymentProfile}
                                className="p-0 m-0 color-accent-primary d-flex justify-content-start align-items-center gap-2">
                                <i className="fa fa-check" />
                                <u>Verify Payment Method</u>
                            </button>
                            <button
                                disabled="disabled"
                                className="p-0 m-0 color-structure-dark d-flex justify-content-start align-items-center gap-2">
                                <i className="fa fa-spin fa-spinner" />
                                Verifying...
                            </button>
                        </IfElse>
                    </IfElse>
                </div>
            </Drawer>
            {enhancedTriggerChild}
        </>
    );
}

// wrap form onChange HOC
const Form = handleChange(props => {
    const currentYear = new Date().getFullYear();

    return (
        <div className="add-credit-profile-form-03mc__form">
            <InputWithErrorHandler
                onChange={props.onInputChange.bind(this, 'account_number')}
                value={props.formData.account_number || ''}
                placeholder="16 digit card number"
                className="inspectletIgnore mb-4"
                errorPath={'account_number'}
                error={props.error}
                label=""
                newestds
            />
            <div className="d-flex align-items-center justify-content-end gap-4 w-100 mb-4">
                <InputWithErrorHandler
                    onChange={props.onInputChange.bind(this, 'card_code')}
                    className="add-credit-profile-form-03mc__code inspectletIgnore w-100"
                    placeholder="Enter security code"
                    value={props.formData.card_code || ''}
                    errorPath={'card_code'}
                    error={props.error}
                    label=""
                    newestds
                />
                <SelectWithErrorHandler
                    onChange={props.onInputChange.bind(this, 'expires_month')}
                    options={[...Array(12).keys()].map(number => {
                        return {id: number + 1, label: number + 1};
                    })}
                    value={props.formData.expires_month || ''}
                    errorPath={'expires_month'}
                    error={props.error}
                    placeholder="Month"
                    className="w-100"
                    label=""
                    newestds
                />
                <SelectWithErrorHandler
                    options={[...Array(15).keys()].map(number => {
                        const optionYear = currentYear + number;
                        return {id: optionYear, label: optionYear};
                    })}
                    onChange={props.onInputChange.bind(this, 'expires_year')}
                    value={props.formData.expires_year || ''}
                    errorPath={'expires_year'}
                    error={props.error}
                    placeholder="Year"
                    className="w-100"
                    emptyText=" "
                    label=""
                    newestds
                />
            </div>
            {/* Omitting for now */}
            {/*<CheckboxWithErrorHandler
                onChange={props.onCheckboxChange.bind(this, 'is_default')}
                label={'Save as default payment method'}
                checked={props.formData.is_default}
                className="mb-4"
                newestds
            />*/}
            <ErrorDisplay
                defaultErrorMessage="Something went wrong with the verification. Please check the form."
                className="color-alert-negative mb-4"
                displayEntityErrors={false}
                error={props.error}
                listView={true}
            />
        </div>
    );
});
